import { graph, rest } from "@/store/api";
import { RootState } from "@/store/types";
import { ActionTree } from "vuex/types";
import { FaqState } from "./types";
import { gql } from "@apollo/client/core";
import _ from "lodash";

/**
 * Actions for the FAQ module.
 * Handles async operations for FAQ training, testing, and model management.
 */
const actions: ActionTree<FaqState, RootState> = {
  /**
   * Adds a new intent to the training dataset.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {Object} payload.intent - Intent object to add
   * @returns {void}
   */
  FAQ_ADD_NEW_INTENT: (store, { intent }) => {
    return store.commit("SET_ADD_NEW_INTENT", intent);
  },

  /**
   * Tests training data against the latest or specified model.
   * Updates dataset with test results.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {Array} payload.intents - Intents to test
   * @param {string} payload.latestModelId - Model ID to test against
   * @param {boolean} payload.isIgnoreFallbackOthersIntent - Whether to ignore fallback intent
   * @returns {Promise<Array>} Updated dataset with test results
   */
  FAQ_TEST_TRAINING_DATA: (
    { commit, state },
    { intents, latestModelId, isIgnoreFallbackOthersIntent }
  ) => {
    return graph
      .mutate({
        mutation: gql`
          mutation (
            $intents: [JSON]
            $latestModelId: String
            $isIgnoreFallbackOthersIntent: Boolean
          ) {
            faqAPI {
              testTrainingData(
                intents: $intents
                latestModelId: $latestModelId
                isIgnoreFallbackOthersIntent: $isIgnoreFallbackOthersIntent
              )
            }
          }
        `,
        variables: { intents, latestModelId, isIgnoreFallbackOthersIntent },
      })
      .then((response) => {
        const dataset = _.get(response, "data.faqAPI.testTrainingData");
        commit("SET_UPDATED_DATASET", dataset);
        return dataset;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Tests a specific model with its test data.
   * Updates dataset with test results.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.modelId - Model ID to test
   * @returns {Promise<Array>} Test results dataset
   */
  FAQ_TEST_MODEL_WITH_TEST_DATA: ({ commit, state }, { modelId }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($modelId: String!) {
            faqAPI {
              testModelIdWithTestData(modelId: $modelId)
            }
          }
        `,
        variables: { modelId },
      })
      .then((response) => {
        const dataset = _.get(response, "data.faqAPI.testModelIdWithTestData");
        commit("SET_UPDATED_DATASET", dataset);
        return dataset;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Searches unlabeled messages by search term.
   * Commits results to FAQ unlabeled state.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.searchTerm - Search query
   * @returns {Promise<Array>} Matching unlabeled messages
   */
  FAQ_GET_UNLABELED_MESSAGES_BY_SEARCH: ({ commit, state }, { searchTerm }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($searchTerm: String!) {
            faqAPI {
              getUnlabeledMessagesBySearch(searchTerm: $searchTerm)
            }
          }
        `,
        variables: { searchTerm },
      })
      .then((response) => {
        const data = _.get(response, "data.faqAPI.getUnlabeledMessagesBySearch");
        commit("SET_SEARCH_FAQ_UNLABELED", { questions: data });
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Fetches all unlabeled messages from the API.
   *
   * @param {ActionContext} context - Vuex action context
   * @returns {Promise<void>}
   */
  FETCH_FAQ_UNLABELED: ({ commit, state }) => {
    return rest("get", "faq_unlabeled_messages", {}).then((data) => {
      commit("SET_FAQ_UNLABELED", { questions: data });
    });
  },

  /**
   * Trains the FAQ model using selected topics.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {Array<string>} payload.selectedTopics - Topics to train
   * @returns {Promise<boolean>} True if training started successfully
   */
  FAQ_TRAIN_BY_TOPICS: (state, { selectedTopics }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($selectedTopics: [String!]) {
            faqAPI {
              trainByTopics(selectedTopics: $selectedTopics)
            }
          }
        `,
        variables: { selectedTopics },
      })
      .then((response) => {
        const isSuccessful = _.get(response, "data.faqAPI.trainByTopics");
        return isSuccessful;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Trains the FAQ model using all topics as intents.
   *
   * @param {ActionContext} context - Vuex action context
   * @returns {Promise<boolean>} True if training started successfully
   */
  FAQ_TRAIN_ALL_TOPICS_AS_INTENTS: (state) => {
    return graph
      .mutate({
        mutation: gql`
          mutation {
            faqAPI {
              trainAllTopicsAsIntents
            }
          }
        `,
      })
      .then((response) => {
        const isSuccessful = _.get(response, "data.faqAPI.trainAllTopicsAsIntents");
        return isSuccessful;
      })
      .catch((err) => {
        throw err;
      });
  },
};

export default actions;
